import React, { useEffect, useState } from 'react';
import { Button, Loading } from '..';
import { useDispatch, useSelector } from 'react-redux';

import analytic, {
  analyticEvents,
  analyticTypes,
} from '../../service/analytic';
import {
  resetAuthState,
  signinWithFB,
  signinWithGoogle,
} from '../../redux/actions/auth';
import PhoneAuthentication from './PhoneAuthentication';
import EmailAuthentication from './EmailAuthentication';

import styles from './LoginDialog.module.css';
import icArrowBack from '../../assets/ic-arrow-back.svg';
import icClose from '../../assets/ic-close.svg';
import icPhone from '../../assets/ic-phone.svg';
import icEmail from '../../assets/ic-email.svg';
import icFb from '../../assets/ic-fb.svg';
import icGg from '../../assets/ic-gg.svg';
import platform from 'platform';

const checkWebBrowser = () => {
  const os = platform.os.family?.toLocaleLowerCase();
  const browser = platform.ua?.toLocaleLowerCase();

  if (os === 'ios') {
    return !browser?.includes('safari');
  } else {
    return browser?.includes('wv');
  }
};

const isWebView = checkWebBrowser();

const contents = {
  login: {
    title: 'Login',
    buttons: [
      {
        id: 'phone',
        text: 'Login with Mobile',
        icon: icPhone,
        color: '#F49832',
      },
      {
        id: 'email',
        text: 'Login with Email',
        icon: icEmail,
        color: '#353535',
      },
      {
        id: 'fb',
        text: 'Login with Facebook',
        icon: icFb,
        color: '#0A61B0',
      },
      ...(isWebView
        ? []
        : [
            {
              id: 'gg',
              text: 'Login with Google',
              icon: icGg,
              color: '#E83D28',
            },
          ]),
    ],
    footer: {
      message: "Don't have an account?",
      button: 'Register Now',
    },
  },
  register: {
    title: 'Register',
    buttons: [
      {
        id: 'phone',
        text: 'Register with Mobile',
        icon: icPhone,
        color: '#F49832',
      },
      {
        id: 'email',
        text: 'Register with Email',
        icon: icEmail,
        color: '#353535',
      },
      {
        id: 'fb',
        text: 'Register with Facebook',
        icon: icFb,
        color: '#0A61B0',
      },
      ...(isWebView
        ? []
        : [
            {
              id: 'gg',
              text: 'Register with Google',
              icon: icGg,
              color: '#E83D28',
            },
          ]),
    ],
    footer: {
      message: 'Already have an account?',
      button: 'Login Now',
    },
  },
};

const RenderAuth = ({ authWith, ...otherProps }) => {
  switch (authWith) {
    case 'email':
      return <EmailAuthentication {...otherProps} />;
    case 'phone':
      return <PhoneAuthentication {...otherProps} />;
    default:
      return null;
  }
};

const LoginDialog = ({ onClose }) => {
  const [dialogState, setDialogState] = useState('login');
  const [authWith, setAuthWith] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { loading, sessionStatus } = auth;

  const handleGoBack = () => {
    setShowResetPassword(false);
    setAuthWith();
  };

  const dialogStateToggle = () => {
    setDialogState((state) => {
      return state === 'login' ? 'register' : 'login';
    });
    handleGoBack();
  };

  const handleCloseModal = () => {
    onClose();
    handleGoBack();
  };

  const handleClickOption = async (id) => {
    switch (id) {
      case 'phone':
      case 'email':
        return setAuthWith(id);

      case 'fb':
        analytic(
          analyticTypes.event,
          analyticEvents.AUTH.START_LOGIN_BY_FACEBOOK
        );
        await dispatch(signinWithFB());
        return onClose();

      case 'gg':
        analytic(
          analyticTypes.event,
          analyticEvents.AUTH.START_LOGIN_BY_GOOGLE
        );
        await dispatch(signinWithGoogle());
        return onClose();

      default:
        return;
    }
  };

  useEffect(() => {
    analytic(
      analyticTypes.trackPage,
      dialogState === 'login' ? 'LoginOptions' : 'RegisterOption'
    );
  }, [dispatch, dialogState]);

  useEffect(() => {
    if (dialogState || authWith || showResetPassword)
      dispatch(resetAuthState());
    return () => {
      dispatch(resetAuthState());
    };
  }, [dispatch, dialogState, authWith, showResetPassword]);

  return (
    <div className={styles['container']}>
      <div className={styles['header']}>
        {authWith && (
          <img src={icArrowBack} alt='back' onClick={handleGoBack} />
        )}
        <h1>
          {authWith
            ? contents[dialogState].buttons.find(({ id }) => id === authWith)
                .text
            : contents[dialogState].title}
        </h1>
        <img src={icClose} alt='close' onClick={handleCloseModal} />
      </div>
      {loading && authWith !== 'phone' ? (
        <div className={styles['loading-wrapper']}>
          <Loading />
          {sessionStatus === 'validating' ? (
            <p>Validating session</p>
          ) : (
            <p>Authenticating</p>
          )}
        </div>
      ) : authWith ? (
        <RenderAuth
          authWith={authWith}
          type={dialogState}
          showResetPassword={showResetPassword}
          setShowResetPassword={setShowResetPassword}
          onCloseModal={handleCloseModal}
          onGoBack={handleGoBack}
        />
      ) : (
        <>
          <div className={styles['options']}>
            {contents[dialogState].buttons.map((button) => {
              return (
                <Button
                  key={button.id}
                  icon={button.icon}
                  iconStyle={{
                    width: 24,
                    marginLeft: 20,
                    marginRight: 50,
                  }}
                  color={button.color}
                  size='large'
                  style={{
                    padding: '1rem 2rem',
                    margin: '1rem 0',
                    width: '100%',
                    fontSize: 16,
                    fontWeight: 600,
                    justifyContent: 'flex-start',
                  }}
                  onClick={() => handleClickOption(button.id, dialogState)}
                >
                  {button.text}
                </Button>
              );
            })}
            {!!auth.err && <div className='error'>{auth.err}</div>}
          </div>
        </>
      )}
      <div
        className={`${styles['footer']} ${authWith && styles['inline-list']}`}
      >
        <span>{contents[dialogState].footer.message}</span>
        <span className={styles['state-toggle']} onClick={dialogStateToggle}>
          {contents[dialogState].footer.button}
        </span>
      </div>
    </div>
  );
};

export default LoginDialog;
